import { countSigFigs } from './sigfigs';

/**
 * A small seeded generator, so any problem can be reproduced exactly from the
 * seed stored with the attempt.
 */

export interface Rng {
  (): number;
  seed: string;
}

/** mulberry32: small, fast, and good enough for making up practice numbers. */
export function makeRng(seed: string): Rng {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i += 1) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;

  const rng = (() => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }) as Rng;

  rng.seed = seed;
  return rng;
}

export function newSeed(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('');
}

/** A whole number from min to max, both ends included. */
export function randInt(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/**
 * A value rounded to exactly `figures` significant figures, returned with its
 * text so the trailing zeros survive into the prompt.
 */
export function randFloat(
  rng: Rng,
  min: number,
  max: number,
  figures: number,
): { value: number; text: string } {
  const raw = min + rng() * (max - min);
  const exponent = Math.floor(Math.log10(Math.abs(raw) || 1));
  const places = Math.max(0, figures - 1 - exponent);
  let text = raw.toFixed(places);

  // "250" written for three figures reads as two, because a bare integer's
  // trailing zeros are ambiguous. A trailing point fixes it where it can, and
  // where it cannot, scientific notation says it plainly.
  if (countSigFigs(text).count !== figures) {
    const withPoint = `${text}.`;
    text =
      countSigFigs(withPoint).count === figures
        ? withPoint
        : `${(raw / 10 ** exponent).toFixed(figures - 1)}e${exponent}`;
  }

  return { value: Number(text.endsWith('.') ? text.slice(0, -1) : text), text };
}

export function pick<T>(rng: Rng, items: readonly T[]): T {
  if (items.length === 0) throw new Error('Nothing to pick from.');
  return items[Math.floor(rng() * items.length)]!;
}

export function shuffled<T>(rng: Rng, items: readonly T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const a = out[i]!;
    const b = out[j]!;
    out[i] = b;
    out[j] = a;
  }
  return out;
}
